import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { TriangleAlert } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TextInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { colors } from "../../theme";
import { UserRole } from "../../types";

interface DeleteClinicResponse {
  deleted?: { clinic_name?: string };
}

interface DeleteClinicRequest {
  confirm: string;
  force: boolean;
}

const DeleteClinic: React.FC<{ clinicName: string }> = ({ clinicName }) => {
  const { user } = useAuth();
  const { apiService, setSubmitting } = useApi();
  const { translate } = useApp();
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [force, setForce] = useState(false);

  const t = (key: string, params?: Record<string, string>) =>
    translate("manage", `sections.danger.${key}`, params);

  // Cross-clinic deletion is a Super Admin capability; gate the UI to match.
  if (user?.role !== UserRole.SuperAdmin) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setConfirmText("");
    setForce(false);
  };

  const confirmed = confirmText.trim() === clinicName && clinicName.length > 0;

  const handleDelete = async () => {
    if (!confirmed) {
      showError(t("errors.mismatch"));
      return;
    }
    try {
      setSubmitting(true);
      await apiService.delete<DeleteClinicResponse, DeleteClinicRequest>(
        "/clinic",
        { confirm: confirmText.trim(), force }
      );
      showSuccess(t("messages.success", { clinic: clinicName }));
      // The acted-on clinic no longer exists; drop any override and reload.
      apiService.setClinicOverride(null);
      close();
      navigate("/");
      window.location.reload();
    } catch (error: any) {
      showError(t("messages.fail"));
      console.error("Failed to delete clinic", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ p: 4, border: `1px solid ${colors.red[200]}` }}>
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
        <Stack spacing={0}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <TriangleAlert color={colors.red[500]} size={18} />
            <Typography variant={"medium"} color={colors.red[700]}>
              {t("title")}
            </Typography>
          </Stack>
          <Typography fontSize={"sm"} variant={"subtitle1"}>
            {t("subtitle")}
          </Typography>
        </Stack>
        <Box width={160}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              backgroundColor: colors.red[600],
              color: "#fff",
              "&:hover": { backgroundColor: colors.red[700] },
            }}
          >
            {t("buttons.delete")}
          </Button>
        </Box>
      </Stack>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant={"medium"}>{t("dialog.title")}</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant={"body2"}>
              {t("dialog.warning", { clinic: clinicName })}
            </Typography>
            <TextInput
              fullWidth
              label={t("dialog.confirm_label", { clinic: clinicName })}
              value={confirmText}
              onChange={(value: string | number) =>
                setConfirmText(value as string)
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={force}
                  onChange={(event) => setForce(event.target.checked)}
                />
              }
              label={t("dialog.force_label")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} color="whiteButton">
            {t("dialog.buttons.cancel")}
          </Button>
          <Box width={200}>
            <SubmitButton
              onClick={handleDelete}
              disabled={!confirmed}
              sx={{
                backgroundColor: colors.red[600],
                color: "#fff",
                "&:hover": { backgroundColor: colors.red[700] },
              }}
            >
              {t("dialog.buttons.confirm")}
            </SubmitButton>
          </Box>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DeleteClinic;
